// Damage numbers: instanced quads that read their digits from a canvas atlas (fill mask in R,
// fill+ink outline in G), so a number costs one ring slot and every number is one draw.
// They rise, pop and fade at a near-constant screen size and always draw on top.

import * as THREE from 'three';
import { Ring, quadGeometry, premulMaterial } from './gpu';
import type { RGB } from './particles';

const STRIDE = 16;

function makeAtlas() {
  const cw = 64, ch = 96;
  const W = cw * 10;
  const make = () => { const c = document.createElement('canvas'); c.width = W; c.height = ch; return c; };
  const a = make(), b = make();
  const font = `700 82px "Space Grotesk", "Arial Black", sans-serif`;
  const ca = a.getContext('2d')!, cb = b.getContext('2d')!;
  for (const c of [ca, cb]) { c.font = font; c.textAlign = 'center'; c.textBaseline = 'middle'; c.lineJoin = 'round'; }
  for (let d = 0; d < 10; d++) {
    const x = d * cw + cw / 2, y = ch / 2 + 4;
    ca.lineWidth = 15; ca.strokeStyle = '#fff'; ca.strokeText(String(d), x, y); ca.fillStyle = '#fff'; ca.fillText(String(d), x, y);
    cb.fillStyle = '#fff'; cb.fillText(String(d), x, y);
  }
  const ia = ca.getImageData(0, 0, W, ch).data, ib = cb.getImageData(0, 0, W, ch).data;
  const out = new Uint8Array(W * ch * 4);
  for (let y = 0; y < ch; y++) for (let x = 0; x < W; x++) {
    const si = (y * W + x) * 4, di = ((ch - 1 - y) * W + x) * 4; // flip for GL
    out[di] = ib[si + 3]; out[di + 1] = ia[si + 3]; out[di + 2] = 0; out[di + 3] = 255;
  }
  const t = new THREE.DataTexture(out, W, ch, THREE.RGBAFormat);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

const VS = /* glsl */ `
attribute vec4 aA; // pos, t0
attribute vec4 aB; // value, digits, scale, life
attribute vec4 aC; // fill rgb, lane lift (world m, from the CPU layout)
attribute vec4 aD; // outline rgb, seed
uniform float uTime;
varying vec2 vUv;
varying float vN;
varying float vVal;
varying vec3 vFill;
varying vec3 vInk;
varying float vA;
void main() {
  float age = uTime - aA.w;
  float life = aB.w;
  if ( age < 0.0 || age > life ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
  float lt = age / life;
  vec3 right = vec3( viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0] );
  vec3 up = vec3( viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1] );
  float dist = length( aA.xyz - cameraPosition );
  float h = max( dist * 0.034, 0.22 ) * aB.z;
  float pop = 1.0 + 0.7 * exp( -age * 16.0 ) - 0.15 * smoothstep( 0.7, 1.0, lt );
  // rise, plus the lane the CPU layout gave it so concurrent numbers never share glyph space
  // (no random sideways drift: that is what ran neighbours together into strings)
  vec3 c = aA.xyz + up * ( 0.35 + h * 1.6 * ( 1.0 - exp( -3.2 * age ) ) + aC.w );
  float w = h * 0.6 * aB.y;
  vec3 wp = c + ( right * position.x * w + up * position.y * h ) * pop;
  gl_Position = projectionMatrix * viewMatrix * vec4( wp, 1.0 );
  // keep off the reticle: the whole number slides out of a small disc around screen centre
  vec4 cc = projectionMatrix * viewMatrix * vec4( c, 1.0 );
  if ( cc.w > 0.0 ) {
    float asp = projectionMatrix[1][1] / projectionMatrix[0][0];
    vec2 n = cc.xy / cc.w; vec2 na = vec2( n.x * asp, n.y );
    float ln = length( na );
    float R = 0.2;
    if ( ln < R ) {
      vec2 dir = ln > 1e-4 ? na / ln : vec2( 0.0, 1.0 );
      vec2 to = dir * R; to.x /= asp;
      gl_Position.xy += ( to - n ) * gl_Position.w;
    }
  }
  vUv = position.xy + 0.5;
  vN = aB.y; vVal = aB.x;
  vFill = aC.rgb; vInk = aD.rgb;
  vA = 1.0 - smoothstep( 0.62, 1.0, lt );
}
`;

const FS = /* glsl */ `
uniform sampler2D tAtlas;
varying vec2 vUv;
varying float vN;
varying float vVal;
varying vec3 vFill;
varying vec3 vInk;
varying float vA;
void main() {
  float fi = vUv.x * vN;
  float i = floor( fi );
  float k = vN - 1.0 - i;
  float d = mod( floor( ( vVal + 0.5 ) / pow( 10.0, k ) ), 10.0 );
  vec2 uv = vec2( ( d + fract( fi ) ) / 10.0, vUv.y );
  vec4 t = texture2D( tAtlas, uv );
  float a = t.g * vA;
  if ( a < 0.02 ) discard;
  vec3 col = mix( vInk, vFill, smoothstep( 0.35, 0.65, t.r ) );
  gl_FragColor = vec4( col * a, a );
}
`;

export class Numbers {
  readonly mesh: THREE.Mesh;
  readonly ring: Ring;
  readonly uniforms = { uTime: { value: 0 } };
  time = 0;
  constructor(cap: number) {
    this.ring = new Ring(cap, STRIDE);
    const r = this.ring;
    const g = quadGeometry(cap);
    g.setAttribute('aA', r.attr(4, 0));
    g.setAttribute('aB', r.attr(4, 4));
    g.setAttribute('aC', r.attr(4, 8));
    g.setAttribute('aD', r.attr(4, 12));
    for (let i = 0; i < cap; i++) r.arr[i * STRIDE + 3] = -1e6;
    const m = premulMaterial(new THREE.ShaderMaterial({
      vertexShader: VS, fragmentShader: FS,
      uniforms: { ...this.uniforms, tAtlas: { value: typeof document !== 'undefined' ? makeAtlas() : null } },
    }));
    m.depthTest = false;
    // the atlas bakes at once with whatever face is ready; rebake when Space Grotesk lands
    if (typeof document !== 'undefined' && document.fonts) {
      document.fonts.load('700 82px "Space Grotesk"').then((f) => {
        if (!f.length) return;
        const u = m.uniforms.tAtlas, old = u.value as THREE.Texture | null;
        u.value = makeAtlas(); old?.dispose();
      }).catch(() => {});
    }
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 50;
  }
  // live numbers (CPU mirror) for merging hits per target and laying numbers out without overlap
  private static readonly N = 24;
  private rSlot = new Int32Array(Numbers.N).fill(-1); private rT = new Float32Array(Numbers.N).fill(-1e9);
  private rT0 = new Float32Array(Numbers.N).fill(-1e9); private rLife = new Float32Array(Numbers.N);
  private rX = new Float32Array(Numbers.N); private rY = new Float32Array(Numbers.N); private rZ = new Float32Array(Numbers.N);
  private rV = new Float32Array(Numbers.N); private rP = new Int8Array(Numbers.N); private rMine = new Uint8Array(Numbers.N);
  // screen rect at full rise, NDC (centre x/y, half w/h)
  private rCx = new Float32Array(Numbers.N); private rCy = new Float32Array(Numbers.N);
  private rHw = new Float32Array(Numbers.N); private rHh = new Float32Array(Numbers.N);
  private cam: THREE.Camera | null = null;
  private v = new THREE.Vector3();
  private live(i: number, now: number) { return this.rSlot[i] >= 0 && now - this.rT0[i] < this.rLife[i]; }
  private digits(v: number) { return v >= 10000 ? 5 : v >= 1000 ? 4 : v >= 100 ? 3 : v >= 10 ? 2 : 1; }
  /** world height of a number (mirrors the VS) */
  private height(x: number, y: number, z: number, scale: number) {
    const c = this.cam;
    const d = c ? Math.hypot(x - c.position.x, y - c.position.y, z - c.position.z) : 10;
    return Math.max(d * 0.034, 0.22) * scale;
  }
  /** NDC rect of a number at full rise with a given lane lift; false if behind the camera */
  private rect(x: number, y: number, z: number, h: number, digits: number, lift: number, out: Float32Array) {
    const c = this.cam as THREE.PerspectiveCamera | null;
    if (!c) { out[0] = 0; out[1] = 0; out[2] = 0; out[3] = 0; return false; }
    // the VS rises along the camera's up vector; world y is close enough for layout
    const v = this.v.set(x, y + 0.35 + h * 1.6 + lift, z);
    v.applyMatrix4(c.matrixWorldInverse);
    const w = -v.z;
    if (w <= 0.05) return false;
    const P = c.projectionMatrix.elements;
    out[0] = (P[0] * v.x) / w; out[1] = (P[5] * v.y) / w;
    out[3] = (0.5 * h * P[5]) / w * 1.15; // + a little air between lines
    out[2] = (0.5 * h * 0.6 * digits * P[0]) / w + 0.01;
    return true;
  }
  private tmpR = new Float32Array(4);
  /**
   * prio: style priority (crit > head > others); a merge keeps the strongest style seen.
   * Hits on the same target (within 2 m, last hit < 0.6 s ago, number still up) add into one rising
   * total. New numbers take the first lane up that overlaps no live number and keeps off the
   * reticle. At most `maxUp` numbers are up; `force` (mine / crit) evicts the oldest other's.
   */
  spawn(x: number, y: number, z: number, value: number, scale: number, life: number, fill: RGB, ink: RGB, prio = 0, force = true, maxUp = 8, mine = false) {
    const now = this.time, N = Numbers.N, a = this.ring.arr;
    let up = 0, best = -1, bestD = 4, oldestOther = -1;
    for (let i = 0; i < N; i++) {
      if (!this.live(i, now)) continue;
      up++;
      if (!this.rMine[i] && (oldestOther < 0 || this.rT0[i] < this.rT0[oldestOther])) oldestOther = i;
      if (now - this.rT[i] > 0.6 || this.rMine[i] !== (mine ? 1 : 0)) continue;
      const d2 = (this.rX[i] - x) ** 2 + (this.rY[i] - y) ** 2 + (this.rZ[i] - z) ** 2;
      if (d2 < bestD) { bestD = d2; best = i; }
    }
    if (best >= 0) {
      const i = best, v = Math.min(99999, Math.round(this.rV[i] + value));
      const o = this.rSlot[i] * STRIDE, n = this.digits(v), n0 = Math.max(1, a[o + 5]);
      this.rV[i] = v; this.rT[i] = now;
      this.rHw[i] = (this.rHw[i] - 0.01) * n / n0 + 0.01; // a longer total takes more room
      a[o + 4] = v; a[o + 5] = n;
      if (prio > this.rP[i]) {
        this.rP[i] = prio; a[o + 6] = scale; a[o + 7] = Math.max(a[o + 7], life);
        a[o + 8] = fill[0]; a[o + 9] = fill[1]; a[o + 10] = fill[2]; a[o + 12] = ink[0]; a[o + 13] = ink[1]; a[o + 14] = ink[2];
      }
      // keep it up while the hits keep coming: restart its clock at the risen pose (no re-drop)
      const keep = Math.min(now - a[o + 3], 0.35);
      a[o + 3] = now - keep; this.rT0[i] = a[o + 3]; this.rLife[i] = a[o + 7];
      this.ring.touch(this.rSlot[i]);
      return;
    }
    if (up >= maxUp) {
      if (!force || oldestOther < 0) return;
      // make room: end the oldest number that isn't the player's
      const o = this.rSlot[oldestOther] * STRIDE;
      a[o + 7] = Math.max(0, now - a[o + 3]); this.rLife[oldestOther] = 0; this.ring.touch(this.rSlot[oldestOther]);
    }
    const v = Math.max(0, Math.min(99999, Math.round(value)));
    const n = this.digits(v);
    const h = this.height(x, y, z, scale);
    // lane layout: lift in steps of one line height until nothing live overlaps and the reticle is clear
    const R = this.tmpR;
    let lift = 0;
    const asp = this.cam ? (this.cam as THREE.PerspectiveCamera).projectionMatrix.elements[5] / (this.cam as THREE.PerspectiveCamera).projectionMatrix.elements[0] : 1;
    for (let lane = 0; lane < 8; lane++) {
      lift = lane * h * 1.2;
      if (!this.rect(x, y, z, h, n, lift, R)) break;
      let hit = false;
      for (let i = 0; i < N && !hit; i++) {
        if (!this.live(i, now)) continue;
        if (Math.abs(this.rCx[i] - R[0]) < this.rHw[i] + R[2] && Math.abs(this.rCy[i] - R[1]) < this.rHh[i] + R[3]) hit = true;
      }
      // the reticle disc (aspect-corrected, matches the VS push radius)
      if (!hit && Math.hypot(R[0] * asp, R[1]) < 0.2 + R[3]) hit = true;
      if (!hit) break;
    }
    const o = this.ring.alloc();
    let h_ = -1;
    for (let i = 0; i < N; i++) if (!this.live(i, now)) { h_ = i; break; }
    if (h_ < 0) h_ = oldestOther >= 0 ? oldestOther : 0;
    this.rSlot[h_] = o / STRIDE; this.rT[h_] = now; this.rT0[h_] = now; this.rLife[h_] = life;
    this.rX[h_] = x; this.rY[h_] = y; this.rZ[h_] = z; this.rV[h_] = v; this.rP[h_] = prio; this.rMine[h_] = mine ? 1 : 0;
    this.rCx[h_] = R[0]; this.rCy[h_] = R[1]; this.rHw[h_] = R[2]; this.rHh[h_] = R[3];
    a[o] = x; a[o + 1] = y; a[o + 2] = z; a[o + 3] = now;
    a[o + 4] = v; a[o + 5] = n; a[o + 6] = scale; a[o + 7] = life;
    a[o + 8] = fill[0]; a[o + 9] = fill[1]; a[o + 10] = fill[2]; a[o + 11] = lift;
    a[o + 12] = ink[0]; a[o + 13] = ink[1]; a[o + 14] = ink[2]; a[o + 15] = 0.5;
  }
  update(time: number, camera?: THREE.Camera) {
    this.time = time;
    if (camera) this.cam = camera;
    this.uniforms.uTime.value = time;
    this.ring.flush();
  }
}
